import { useShallow } from 'zustand/react/shallow';

import { useRegionStore } from '@packages/stores';
import { BOOST_CATEGORY, BOOST_CODE } from '@packages/types';
import {
  convertCentsToDollarsAndFormat,
  getDestinationsLabel,
  getFullDateStr,
} from '@packages/utils';

import { Button } from '@elements/button';
import { Text } from '@elements/text';
import { i18n } from '@i18n';
import { useModalStore, useTripStore } from '@store';
import { testProps } from '@utils';

import { DefaultBoostText } from './defaultBoostText.component';
import { EMCBoostText } from './emcBoostText.component';
import { ExcessText } from './excessText.component';
import { ExtraCancellationText } from './extraCancellationText.component';
import { SpecifiedItemsText } from './specifiedItemsText.component';
import { useTravellersText } from './useTravellersText.hook';

export type PolicyDetailsProps = {
  isModal?: boolean;
};

export const PolicyDetails = ({ isModal = false }: PolicyDetailsProps) => {
  const tripBannerImage = useTripStore(state => state.trip.destinations[0]?.imageLinks?.tablet);
  const tripDestinations = useTripStore(state => getDestinationsLabel(state.trip));
  const country = useRegionStore(state => state.region?.country);
  const closeModal = useModalStore(state => state.closeModal);
  const travelDatesText = useTripStore(
    state =>
      `${getFullDateStr(state.trip.startDate, country)} - ${getFullDateStr(state.trip.endDate, country)}`,
  );
  const travellersText = useTravellersText();

  const baseCoverText = useTripStore(state => {
    const mandatoryBoost = state.trip.boosts.find(b => b.isMandatory);
    const plural = mandatoryBoost?.duration && mandatoryBoost.duration > 1 ? 's' : '';
    return `${mandatoryBoost?.name}: ${mandatoryBoost?.duration} day${plural}`;
  });

  const policyNumber = useTripStore(state => state.trip.policy?.policyNumber);
  const tripCost = useTripStore(state => state.trip.totalTripCost);
  const addedDailyBoostCodes = useTripStore(
    useShallow(state => {
      return state.trip?.boosts
        ?.filter(b => b.isAdded && b.category === BOOST_CATEGORY.MY_ACTIVITIES)
        .map(b => b.code) as BOOST_CODE[] | undefined;
    }),
  );

  const addedTripExtrasBoostCodes = useTripStore(
    useShallow(state => {
      return state.trip?.boosts
        ?.filter(b => b.isAdded && b.category === BOOST_CATEGORY.MY_STUFF)
        .map(b => b.code) as BOOST_CODE[] | undefined;
    }),
  );

  return (
    <div className="space-y-s16 bg-surface-primary p-s16 rounded-br/md">
      {!!tripBannerImage && !isModal && (
        <img
          src={tripBannerImage}
          className="h-52 w-full object-cover  rounded-br/md"
          alt="Trip Banner"
        />
      )}
      <Text {...testProps('policyDetails-header')} variant={'subTitle-20/sb'}>
        {i18n.t('policyDetails.header')}
      </Text>
      <div className="space-y-s8">
        <Text {...testProps('policyDetails-tripDestinations')} variant={'body-16/sb'}>
          {tripDestinations}
        </Text>
        <Text {...testProps('policyDetails-travelDates')}>{travelDatesText}</Text>
        <Text {...testProps('policyDetails-travellers')} variant={'body-16/sb'}>
          {i18n.t('policyDetails.travellers', { count: travellersText.length })}
        </Text>
        {travellersText.map((traveller, index) => (
          <Text
            className="break-all"
            {...testProps(`policyDetails-travellers-${index}`)}
            key={traveller.travellerId}>
            {traveller.name} ({traveller.age})
          </Text>
        ))}
      </div>
      <div className="border-t border-t-border-bold" />
      {!!policyNumber && (
        <div className="space-y-s8">
          <Text variant={'body-16/sb'}>{i18n.t('policyDetails.policyNumber')}</Text>
          <Text {...testProps('policyDetails-policyNumber')}>{policyNumber}</Text>
        </div>
      )}
      {!!tripCost && (
        <div className="space-y-s8">
          <Text variant={'body-16/sb'}>{i18n.t('policyDetails.tripCost')}</Text>
          <Text {...testProps('policyDetails-tripCost')}>
            {convertCentsToDollarsAndFormat(tripCost * 100)}
          </Text>
        </div>
      )}

      <div className="space-y-s8">
        <Text variant={'body-16/sb'}>{i18n.t('policyDetails.basePolicy')}</Text>
        <Text {...testProps('policyDetails-basePolicy')}>{baseCoverText}</Text>
      </div>
      <ExcessText />
      {!!addedTripExtrasBoostCodes?.length && (
        <div className="space-y-s8">
          <Text variant={'body-16/sb'}>{i18n.t('policyDetails.tripExtras')}</Text>
          {/* AU Boosts */}
          <DefaultBoostText boostCode={BOOST_CODE.GADGET_COVER} />
          <ExtraCancellationText />
          <SpecifiedItemsText />
          {/* US Boosts */}
          <DefaultBoostText boostCode={BOOST_CODE.PRE_TRIP_CANCELLATION} />
          <DefaultBoostText boostCode={BOOST_CODE.LUGGAGE_UPGRADE} />
          <DefaultBoostText boostCode={BOOST_CODE.TRIP_INCONVENIENCE} />
        </div>
      )}
      {!!addedDailyBoostCodes?.length && (
        <div className="space-y-s8">
          <Text variant={'body-16/sb'}>{i18n.t('policyDetails.dailyBoosts')}</Text>
          {addedDailyBoostCodes.map(code => (
            <DefaultBoostText key={code} boostCode={code} />
          ))}
        </div>
      )}
      <EMCBoostText />
      {isModal && (
        <Button
          {...testProps('policy-details-modal-ok-button')}
          className="w-full"
          title="OK"
          variant="primary"
          onClick={closeModal}
        />
      )}
    </div>
  );
};
